import { useEffect } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  Shield,
  Award,
  FileCheck,
  Recycle,
  CheckCircle,
  AlertTriangle,
} from "lucide-react";
import { ChevronRight } from "../components/DirectionalIcon";
import { buildLocalizedPath } from "../constants/languages";
import ComplianceBadge from "../components/ComplianceBadge";
import FAQItem from "../components/FAQItem";

const CompliancePage = () => {
  const { t, i18n } = useTranslation();

  useEffect(() => {
    document.title = t("compliance.meta.title");
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute("content", t("compliance.meta.description"));
    }
  }, [t]);

  const badges = [
    {
      icon: Shield,
      title: t("compliance.badges.badge1.title"),
      registration: "CBDU628298",
      validUntil: "14 March 2029",
      description: t("compliance.badges.badge1.description"),
    },
    {
      icon: FileCheck,
      title: t("compliance.badges.badge2.title"),
      description: t("compliance.badges.badge2.description"),
    },
    {
      icon: Recycle,
      title: t("compliance.badges.badge3.title"),
      description: t("compliance.badges.badge3.description"),
    },
  ];

  const faqs = [
    {
      question: t("compliance.faq.q1.question"),
      answer: t("compliance.faq.q1.answer"),
    },
    {
      question: t("compliance.faq.q2.question"),
      answer: t("compliance.faq.q2.answer"),
    },
    {
      question: t("compliance.faq.q3.question"),
      answer: t("compliance.faq.q3.answer"),
    },
    {
      question: t("compliance.faq.q4.question"),
      answer: t("compliance.faq.q4.answer"),
    },
  ];

  return (
    <div data-testid="compliance-page">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-32 bg-navy text-white">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-gold text-xs uppercase tracking-[0.3em] font-semibold">
              {t("compliance.hero.kicker")}
            </span>
            <h1 className="font-outfit text-4xl sm:text-5xl md:text-6xl font-bold mt-4 mb-6">
              {t("compliance.hero.titleLine1")}<br />
              {t("compliance.hero.titleLine2")}<br />
              <span className="text-gold">{t("compliance.hero.titleLine3")}</span>
            </h1>
            <p className="text-slate-300 text-lg md:text-xl leading-relaxed">
              {t("compliance.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Trust Paragraph — English-only, see CorporatePartnersPage for why */}
      <section className="py-12 bg-slate-50 border-b border-slate-200">
        <div className="container-custom">
          <p className="text-slate-600 leading-relaxed max-w-3xl mx-auto text-center">
            M A Galaxy Int UK Ltd is a registered UK company (No. 13671542, VAT GB425010549), operating under an
            Upper Tier Waste Carrier registration with the Environment Agency (CBDU628298, valid to March 2029) and
            a registered EORI number for international trade. We've operated in the global solar trading sector
            since 2000, with UK operations based in Swadlincote, Derbyshire.
          </p>
        </div>
      </section>

      {/* Compliance Badges */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("compliance.badges.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4">
              {t("compliance.badges.title")}
            </h2>
          </div>

          <div className="grid md:grid-cols-2 gap-8">
            {badges.map((badge, index) => (
              <ComplianceBadge key={badge.title} {...badge} />
            ))}
          </div>
        </div>
      </section>

      {/* Your Protection Section */}
      <section className="py-20 md:py-32">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 lg:gap-20">
            <div>
              <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
                {t("compliance.protection.kicker")}
              </span>
              <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4 mb-6">
                {t("compliance.protection.title")}
              </h2>
              <p className="text-slate-600 leading-relaxed mb-8">
                {t("compliance.protection.description")}
              </p>

              <div className="space-y-4">
                {[
                  t("compliance.protection.item1"),
                  t("compliance.protection.item2"),
                  t("compliance.protection.item3"),
                  t("compliance.protection.item4"),
                  t("compliance.protection.item5"),
                  t("compliance.protection.item6"),
                ].map((item, index) => (
                  <div key={index} className="flex items-start gap-3 text-slate-600">
                    <CheckCircle className="text-green-600 mt-1 flex-shrink-0" size={18} />
                    <span>{item}</span>
                  </div>
                ))}
              </div>
            </div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="bg-navy text-white p-8 md:p-12"
            >
              <h3 className="font-outfit text-2xl font-bold mb-6">
                {t("compliance.risks.title")}
              </h3>
              <p className="text-slate-300 mb-8 leading-relaxed">
                {t("compliance.risks.description")}
              </p>
              <ul className="space-y-4">
                {[
                  t("compliance.risks.item1"),
                  t("compliance.risks.item2"),
                  t("compliance.risks.item3"),
                  t("compliance.risks.item4"),
                  t("compliance.risks.item5"),
                ].map((item, index) => (
                  <li key={index} className="flex items-start gap-3 text-slate-300">
                    <AlertTriangle className="text-red-400 mt-1 flex-shrink-0" size={18} />
                    <span>{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 md:py-32 bg-slate-50">
        <div className="container-custom">
          <div className="text-center mb-16">
            <span className="text-gold text-xs uppercase tracking-[0.2em] font-semibold">
              {t("compliance.faq.kicker")}
            </span>
            <h2 className="font-outfit text-3xl md:text-4xl font-bold text-navy mt-4">
              {t("compliance.faq.title")}
            </h2>
          </div>

          <div className="max-w-3xl mx-auto">
            {faqs.map((faq, index) => (
              <FAQItem key={index} {...faq} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 md:py-32 bg-navy text-white relative noise-overlay">
        <div className="container-custom relative z-10 text-center">
          <h2 className="font-outfit text-3xl md:text-4xl font-bold mb-6">
            {t("compliance.cta.title")}
          </h2>
          <p className="text-slate-400 max-w-2xl mx-auto mb-10">
            {t("compliance.cta.subtitle")}
          </p>
          <Link
            to={buildLocalizedPath("/sell-panels", i18n.language)}
            data-testid="compliance-cta-btn"
            className="inline-flex items-center gap-2 bg-gold text-navy px-8 py-4 text-xs font-bold uppercase tracking-widest hover:bg-gold-light transition-colors"
          >
            {t("compliance.cta.button")}
            <ChevronRight size={16} />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default CompliancePage;
